import { useCallback, useEffect, useState } from 'react'

import { useShowPrompt } from 'civic-champs-shared/core/modal/hooks'
import { useCreateApiNotification } from 'civic-champs-shared/api/hooks'
import { Organization, SubscriptionDetails } from 'organization/types'
import useGetSubscriptionDetails from './useGetSubscriptionDetails'
import { usePutSubscriptionDetails } from './usePutSubscriptionDetails'
import SubscriptionInfoModal from 'organization/component/SubscriptionInfoModal'

export interface SubscriptionDetailsOperations {
  loading: boolean
  reload: () => Promise<void>
  edit: () => Promise<void>
}

const useSubscriptionDetails = (
  organization: Organization,
): [SubscriptionDetails | null, SubscriptionDetailsOperations] => {
  const [getSub, getSubStatus] = useGetSubscriptionDetails()
  const [putSub, putSubStatus] = usePutSubscriptionDetails()

  const [sub, setSub] = useState<SubscriptionDetails | null>(null)

  const reload = useCallback(async () => {
    const newSub = await getSub(organization.id)
    setSub(newSub)
  }, [getSub, organization.id])

  useEffect(() => {
    reload()
  }, [reload])

  const showEditSubPrompt = useShowPrompt(SubscriptionInfoModal)
  const createNotification = useCreateApiNotification()

  const edit = useCallback(async () => {
    const payload = await showEditSubPrompt({ subDetails: sub })

    const notification = createNotification('Saving Subscription Details')
    try {
      const newSub = await putSub(organization.id, payload)
      notification.onSuccess('Subscription Details saved successfully')
      setSub(newSub)
    } catch (e) {
      notification.onError('Failed to save Subscription Details', e)
    }
  }, [showEditSubPrompt, sub, createNotification, putSub, organization.id])

  return [
    sub,
    {
      loading: !getSubStatus.called || getSubStatus.loading || putSubStatus.loading,
      reload,
      edit,
    },
  ]
}

export default useSubscriptionDetails
